import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebaseConfig';
import {
  collection,
  getDocs,
  setDoc,
  deleteDoc,
  doc,
  updateDoc,
  QueryDocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { HotTable } from '@handsontable/react';
import Handsontable from 'handsontable';
import 'handsontable/dist/handsontable.full.min.css';
import AdminDepartmentManageTab, { type CustomerDeptOption } from './AdminDepartmentManageTab';
import { CUSTOMER_INFO_MAP, type CustomerId } from '../config/customerConfig';

const ADMIN_EMAIL = 'admin@swlogis.it.kr';

/** 부서(서브) 계정 — 거래처 표에는 넣지 않음 */
function isDepartmentAccount(data: Record<string, unknown>): boolean {
  return data.accountType === 'department';
}

const HEADERS = ['이메일', '이름', '역할', 'customerId', '운송료탭', '보고서운송료', '품명모드', '품명목록'];
const FIELDS = ['email', 'name', 'role', 'customerId', 'showStatsTab', 'showStatsInReport', 'itemNameFilterType', 'itemNameList'];

const mapDocToTableRow = (docSnap: QueryDocumentSnapshot<DocumentData>): unknown[] => {
  const data = docSnap.data();
  return FIELDS.map(f => {
    if (f === 'showStatsTab' || f === 'showStatsInReport') {
      return typeof data[f] === 'boolean' ? data[f] : false;
    }
    if (f === 'itemNameFilterType') {
      const v = data[f];
      return (v === 'allow' || v === 'block') ? v : 'all';
    }
    if (f === 'itemNameList') {
      const arr = data[f];
      return Array.isArray(arr) ? arr.join(', ') : '';
    }
    return data[f] || '';
  });
};
const ROLE_OPTIONS = ['internal', 'customer', 'admin'];
const ITEM_NAME_FILTER_OPTIONS = ['all', 'allow', 'block']; // 모두 보이게 / 특정만 보이게 / 특정만 숨기기
// customerId는 직접 입력하도록 변경 (드롭다운 제거)
const BOOL_OPTIONS = [true, false];
const MIN_ROWS = 10;

// 커스텀 SelectEditor: true/false 선택 즉시 저장
const ImmediateSelectEditor: any = (Handsontable.editors.TextEditor as any).prototype.extend();

ImmediateSelectEditor.prototype.createElements = function () {
  (Handsontable.editors.TextEditor as any).prototype.createElements.apply(this, arguments as any);
  this.select = document.createElement('select');
  this.select.style.width = '100%';
  this.select.style.height = '100%';
  ['true', 'false'].forEach(opt => {
    const option = document.createElement('option');
    option.value = opt;
    option.text = opt;
    this.select.appendChild(option);
  });
  this.TEXTAREA.style.display = 'none';
  this.TEXTAREA.parentNode.appendChild(this.select);
};

ImmediateSelectEditor.prototype.open = function () {
  (Handsontable.editors.TextEditor as any).prototype.open.apply(this, arguments as any);
  this.select.value = String(this.originalValue);
  this.select.addEventListener('change', () => {
    this.setValue(this.select.value);
    setTimeout(() => {
      this.finishEditing();
    }, 0);
  });
  this.select.focus();
};

ImmediateSelectEditor.prototype.close = function () {
  (Handsontable.editors.TextEditor as any).prototype.close.apply(this, arguments as any);
  if (this.select) {
    this.select.remove();
    this.select = null;
  }
};

type AdminMainTab = 'clients' | 'departments' | 'manage';

/** 부서 = 동일 customerId의 서브 로그인 (users.accountType === 'department', 백엔드로 Auth 생성) */
interface DepartmentAccountRow {
  email: string;
  /** 폼「부서 이름」(보세·수입 등). 미입력 시 백엔드가 이메일 앞부분으로 둠 */
  displayName: string;
  /** 생성 시 Firestore에 저장된 관리용 평문(기존 행은 없을 수 있음) */
  passwordPlain?: string;
  createdAt?: unknown;
}

const AdminPage = () => {
  const navigate = useNavigate();
  const [adminTab, setAdminTab] = useState<AdminMainTab>('clients');
  const [tableData, setTableData] = useState<any[][]>([]);
  const [loading, setLoading] = useState(true);
  const hotRef = useRef<any>(null);

  /** role=customer 인 users를 customerId로 묶어 부서 탭 드롭다운 구성 */
  const [customerDeptOptions, setCustomerDeptOptions] = useState<CustomerDeptOption[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [departmentAccounts, setDepartmentAccounts] = useState<DepartmentAccountRow[]>([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [newSubEmail, setNewSubEmail] = useState('');
  const [newSubPassword, setNewSubPassword] = useState('');
  const [newSubName, setNewSubName] = useState('');
  const [deptSaving, setDeptSaving] = useState(false);

  /** 부서/관리 탭 거래처 드롭다운: customerConfig 회사명 우선, 부서 계정 이름은 라벨에 쓰지 않음 */
  const loadCustomerDeptOptions = useCallback(async () => {
    setCompaniesLoading(true);
    try {
      const snap = await getDocs(collection(db, 'users'));
      const byCid = new Map<string, string[]>();
      snap.docs.forEach(d => {
        const data = d.data() as Record<string, unknown>;
        if (String(data.role || '') !== 'customer') return;
        const cid = String(data.customerId || '').trim();
        if (!cid) return;
        if (!byCid.has(cid)) byCid.set(cid, []);
        if (isDepartmentAccount(data)) return;
        const n = String(data.name || '').trim();
        if (n) byCid.get(cid)!.push(n);
      });
      const list: CustomerDeptOption[] = [];
      byCid.forEach((mainNames, customerId) => {
        const cfgName = CUSTOMER_INFO_MAP[customerId as CustomerId]?.name;
        const sorted = [...mainNames].sort((a, b) => a.localeCompare(b, 'ko'));
        const displayName = cfgName || sorted[0] || customerId;
        list.push({ customerId, label: displayName });
      });
      list.sort((a, b) => a.label.localeCompare(b.label, 'ko'));
      setCustomerDeptOptions(list);
    } catch {
      setCustomerDeptOptions([]);
    } finally {
      setCompaniesLoading(false);
    }
  }, []);

  useEffect(() => {
    // 로그인된 사용자가 아니거나, 관리자가 아니면 홈으로 리다이렉트
    if (!auth.currentUser || auth.currentUser.email !== ADMIN_EMAIL) {
      alert('관리자만 접근 가능합니다.');
      navigate('/');
    }
  }, []);

  // Firestore에서 사용자 목록 불러오기 (부서 서브계정 제외 — 부서 탭 전용)
  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const snap = await getDocs(collection(db, 'users'));
        const users = snap.docs
          .filter(d => !isDepartmentAccount(d.data() as Record<string, unknown>))
          .map(d => mapDocToTableRow(d));
        while (users.length < MIN_ROWS) users.push(['', '', 'customer', '', false, false, 'all', '']);
        setTableData(users);
        await loadCustomerDeptOptions();
      } finally {
        setLoading(false);
      }
    };
    void fetchUsers();
  }, [loadCustomerDeptOptions]);

  const reloadDepartmentAccounts = useCallback(async (customerId: string) => {
    if (!customerId) {
      setDepartmentAccounts([]);
      return;
    }
    setDepartmentsLoading(true);
    try {
      const snap = await getDocs(collection(db, 'users'));
      const rows: DepartmentAccountRow[] = [];
      snap.docs.forEach(d => {
        const data = d.data() as Record<string, unknown>;
        if (String(data.role || '') !== 'customer') return;
        if (String(data.customerId || '').trim() !== customerId) return;
        if (data.accountType !== 'department') return;
        rows.push({
          email: d.id,
          displayName: String(data.name || '').trim() || d.id,
          passwordPlain:
            typeof data.loginPasswordPlain === 'string' ? data.loginPasswordPlain : undefined,
          createdAt: data.createdAt,
        });
      });
      rows.sort((a, b) => a.email.localeCompare(b.email));
      setDepartmentAccounts(rows);
    } catch {
      setDepartmentAccounts([]);
    } finally {
      setDepartmentsLoading(false);
    }
  }, []);

  useEffect(() => {
    void reloadDepartmentAccounts(selectedCustomerId);
  }, [selectedCustomerId, reloadDepartmentAccounts]);

  const refreshUsersTable = async () => {
    const snap = await getDocs(collection(db, 'users'));
    const users = snap.docs
      .filter(d => !isDepartmentAccount(d.data() as Record<string, unknown>))
      .map(d => mapDocToTableRow(d));
    while (users.length < MIN_ROWS) users.push(['', '', 'customer', '', false, false, 'all', '']);
    setTableData(users);
    await loadCustomerDeptOptions();
  };

  const handleCreateDepartmentAccount = async () => {
    const email = newSubEmail.trim().toLowerCase();
    const password = newSubPassword;
    const name = newSubName.trim();
    if (!selectedCustomerId) {
      alert('거래처를 먼저 선택하세요.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      alert('로그인용 이메일 형식을 입력하세요.');
      return;
    }
    if (password.length < 6) {
      alert('비밀번호는 6자 이상이어야 합니다.');
      return;
    }
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      alert('로그인이 필요합니다.');
      return;
    }
    setDeptSaving(true);
    try {
      const res = await fetch('/api/admin/customer-sub-accounts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email,
          password,
          customerId: selectedCustomerId,
          name: name || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(typeof data.error === 'string' ? data.error : '계정 생성에 실패했습니다.');
        return;
      }
      setNewSubEmail('');
      setNewSubPassword('');
      setNewSubName('');
      await reloadDepartmentAccounts(selectedCustomerId);
      await refreshUsersTable();
    } catch (e: unknown) {
      const err = e as { message?: string };
      alert('요청 실패: ' + (err?.message || e));
    } finally {
      setDeptSaving(false);
    }
  };

  const handleDeleteDepartmentAccount = async (row: DepartmentAccountRow) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`계정 ${row.email} 을(를) 삭제할까요? (로그인·Firestore 모두 삭제됩니다)`)) return;
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      alert('로그인이 필요합니다.');
      return;
    }
    try {
      const res = await fetch('/api/admin/customer-sub-accounts/remove', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ email: row.email }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(typeof data.error === 'string' ? data.error : '삭제에 실패했습니다.');
        return;
      }
      setDepartmentAccounts(prev => prev.filter(r => r.email !== row.email));
      await refreshUsersTable();
    } catch (e: unknown) {
      const err = e as { message?: string };
      alert('요청 실패: ' + (err?.message || e));
    }
  };

  // 드롭다운·표 스크롤 보정 — 반드시 .admin-page-handsontable-root 하위로 한정 (전역 .handsontable이면 정산일보 등 다른 화면까지 70vh로 망가짐)
  useEffect(() => {
    const id = 'ht-dropdown-style-admin-scoped';
    let style = document.getElementById(id) as HTMLStyleElement | null;
    if (!style) {
      style = document.createElement('style');
      style.id = id;
      document.head.appendChild(style);
    }
    style.textContent = `
      .admin-page-handsontable-root .handsontable .htDropdown .htSelectEditor,
      .admin-page-handsontable-root .handsontable select.htSelectEditor {
        min-width: 120px !important;
        z-index: 9999 !important;
      }
      .admin-page-handsontable-root .handsontable .ht_master .wtHolder {
        max-height: 70vh !important;
        overflow-y: auto !important;
      }
      .admin-page-handsontable-root .handsontable .ht_clone_left .wtHolder {
        max-height: 70vh !important;
        overflow-y: hidden !important;
      }
      .admin-page-handsontable-root .handsontable .ht_clone_left .wtHolder::-webkit-scrollbar {
        width: 0 !important; height: 0 !important;
      }
    `;
    return () => {
      style?.remove();
    };
  }, []);

  // Firestore에 사용자 저장 (이메일을 문서ID로 사용)
  const saveUser = async (userObj: any) => {
    if (!userObj.email) return;
    if (typeof userObj.showStatsTab !== 'boolean') userObj.showStatsTab = false;
    if (typeof userObj.showStatsInReport !== 'boolean') userObj.showStatsInReport = false;
    // 품명 필터: 타입 기본값, 목록은 배열로 저장
    if (userObj.itemNameFilterType !== 'allow' && userObj.itemNameFilterType !== 'block') {
      userObj.itemNameFilterType = 'all';
    }
    if (typeof userObj.itemNameList === 'string') {
      userObj.itemNameList = userObj.itemNameList.split(',').map((s: string) => s.trim()).filter(Boolean);
    }
    if (!Array.isArray(userObj.itemNameList)) userObj.itemNameList = [];
    await setDoc(doc(db, 'users', userObj.email), userObj);
  };

  // 새 행 추가
  const handleAddRow = () => {
    const newRow = ['', '', 'customer', '', false, false, 'all', ''];
    setTableData(prev => [...prev, newRow]);
    
    // 테이블 리렌더링 후 새로 추가된 행으로 스크롤
    setTimeout(() => {
      if (hotRef.current?.hotInstance) {
        const lastRowIndex = tableData.length;
        hotRef.current.hotInstance.selectCell(lastRowIndex, 0);
        hotRef.current.hotInstance.scrollViewportTo(lastRowIndex, 0);
      }
    }, 100);
  };

  // 행 삭제
  const handleDeleteRow = async (rowIndex: number) => {
    const row = tableData[rowIndex];
    const email = row[0];
    if (email) {
      await deleteDoc(doc(db, 'users', email));
    }
    setTableData(prev => prev.filter((_, idx) => idx !== rowIndex));
  };

  // 삭제 확인 함수
  const confirmDelete = (rowIndex: number) => {
    // eslint-disable-next-line no-restricted-globals
    if (confirm('정말로 이 사용자를 삭제하시겠습니까?')) {
      handleDeleteRow(rowIndex);
    }
  };

  // 셀 수정 시 Firestore에 저장
  const handleAfterChange = async (changes: any, source: string) => {
    if (source === 'loadData' || !changes) return;
    let newTable = hotRef.current?.hotInstance?.getData() || tableData;
    for (const [row, col, oldValue, newValue] of changes) {
      const email = newTable[row][0];
      if (!email) continue;
      const userObj: any = {};
      FIELDS.forEach((f, idx) => { userObj[f] = newTable[row][idx] || ''; });
      await saveUser(userObj);
    }
  };

  // 운송료탭/보고서운송료 셀 클릭 시 토글 및 저장 함수 추가 (품명 컬럼은 토글 제외)
  const handleBoolCellToggle = async (rowIdx: number, colIdx: number) => {
    if (colIdx !== 4 && colIdx !== 5) return;
    const field = colIdx === 4 ? 'showStatsTab' : 'showStatsInReport';
    const user = tableData[rowIdx];
    if (!user) return;
    const oldValue = user[colIdx];
    const newValue = !(oldValue === true || oldValue === 'true');
    // UI 반영
    setTableData(prev => {
      const copy = [...prev];
      copy[rowIdx] = [...copy[rowIdx]];
      copy[rowIdx][colIdx] = newValue;
      return copy;
    });
    // Firestore 저장
    try {
      await updateUserField(user[0], field, newValue); // user[0]이 email 또는 id라고 가정
    } catch (e: unknown) {
      const err = e as any;
      alert('저장 실패: ' + (err?.message || err));
    }
  };

  // Firestore 저장 함수(이미 있다면 중복 정의 X)
  async function updateUserField(email: string, field: string, value: any) {
    // Firestore users 컬렉션에서 email로 문서 찾기 (db는 이미 import됨)
    const usersRef = collection(db, 'users');
    const q = await getDocs(usersRef);
    let foundDoc: QueryDocumentSnapshot<DocumentData> | null = null;
    for (const docSnap of q.docs) {
      if (docSnap.data().email === email) {
        foundDoc = docSnap;
        break;
      }
    }
    if (!foundDoc) throw new Error('사용자 문서를 찾을 수 없습니다.');
    await updateDoc(doc(usersRef, foundDoc.id), { [field]: value });
  }

  return (
    <div
      className="admin-page-handsontable-root"
      style={{
        width: '100%',
        maxWidth: '100%',
        margin: 0,
        padding: 'clamp(16px, 2.5vw, 32px)',
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 48px)',
        overflow: 'hidden',
        boxSizing: 'border-box',
        background: '#fff',
        borderRadius: 16,
        boxShadow: '0 2px 16px rgba(0,0,0,0.10)'
      }}
    >
      <h2 style={{ fontWeight: 700, fontSize: 28, color: '#1976d2', marginBottom: 12 }}>관리자 페이지</h2>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: '1px solid #e5e7eb', paddingBottom: 4 }}>
        <button
          type="button"
          onClick={() => setAdminTab('clients')}
          style={{
            padding: '10px 18px',
            fontSize: 16,
            fontWeight: 600,
            border: 'none',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            background: adminTab === 'clients' ? '#1976d2' : '#f1f5f9',
            color: adminTab === 'clients' ? '#fff' : '#475569',
          }}
        >
          거래처
        </button>
        <button
          type="button"
          onClick={() => setAdminTab('departments')}
          style={{
            padding: '10px 18px',
            fontSize: 16,
            fontWeight: 600,
            border: 'none',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            background: adminTab === 'departments' ? '#1976d2' : '#f1f5f9',
            color: adminTab === 'departments' ? '#fff' : '#475569',
          }}
        >
          부서
        </button>
        <button
          type="button"
          onClick={() => setAdminTab('manage')}
          style={{
            padding: '10px 18px',
            fontSize: 16,
            fontWeight: 600,
            border: 'none',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            background: adminTab === 'manage' ? '#1976d2' : '#f1f5f9',
            color: adminTab === 'manage' ? '#fff' : '#475569',
          }}
        >
          관리
        </button>
      </div>

      {adminTab === 'clients' && (
      <>
      <div style={{ fontSize: 16, color: '#444', marginBottom: 16 }}>
        <span style={{ color: '#2e7d32', fontSize: 14 }}>
          품명모드: all=전체 표시, allow=품명목록에 있는 품명만 표시, block=품명목록에 있는 품명만 숨김. 품명목록은 쉼표 구분 (예: 수출, 내수)
        </span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '8px 0 12px 0' }}>
        <h3 style={{ fontWeight: 700, fontSize: 22, color: '#1976d2', margin: 0 }}>거래처 목록</h3>
        <button 
          onClick={handleAddRow}
          style={{
            background: '#1976d2',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            padding: '10px 20px',
            fontSize: 16,
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#1565c0';
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(25, 118, 210, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#1976d2';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(25, 118, 210, 0.3)';
          }}
        >
          + 사용자 추가
        </button>
      </div>
      {loading ? (
        <div style={{ color: '#888', fontSize: 17, margin: '12px 0' }}>거래처 목록을 불러오는 중...</div>
      ) : (
        // 표 영역: 내부 스크롤 전용, 화면 위쪽에 가깝게 배치 (가로는 전체 폭 사용)
        <div style={{ flex: '1 1 0', minHeight: 0, overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
          <HotTable
            ref={hotRef}
            data={tableData}
            colHeaders={HEADERS}
            rowHeaders={true}
            width="100%"
            height="100%"
            licenseKey={process.env.REACT_APP_HANDSONTABLE_LICENSE_KEY || 'non-commercial-and-evaluation'}
            manualRowResize={true}
            manualColumnResize={true}
            autoColumnSize={false}
            autoRowSize={true}
            className="htCenter"
            afterChange={handleAfterChange}
            readOnly={false}
            contextMenu={{
              items: {
                'remove_row': {
                  name: '행 삭제',
                  callback: function(key: string, selection: any) {
                    const rowIndex = selection[0].start.row;
                    confirmDelete(rowIndex);
                  }
                }
              }
            }}
            columns={[
              { data: 0, width: 280 }, // 이메일
              { data: 1, width: 180 }, // 이름
              { data: 2, type: 'dropdown', source: ROLE_OPTIONS, width: 120 }, // 역할
              { data: 3, width: 220 }, // customerId
              {
                // 운송료탭
                renderer: (instance, td, row, col, prop, value) => {
                  td.innerHTML = value === true || value === 'true'
                    ? '<span style="color:#1976d2;font-weight:700;">true</span>'
                    : '<span style="color:#d32f2f;font-weight:700;">false</span>';
                  td.style.textAlign = 'center';
                },
                readOnly: true,
                width: 100
              },
              {
                // 보고서운송료
                renderer: (instance, td, row, col, prop, value) => {
                  td.innerHTML = value === true || value === 'true'
                    ? '<span style="color:#1976d2;font-weight:700;">true</span>'
                    : '<span style="color:#d32f2f;font-weight:700;">false</span>';
                  td.style.textAlign = 'center';
                },
                readOnly: true,
                width: 100
              },
              { data: 6, type: 'dropdown', source: ITEM_NAME_FILTER_OPTIONS, width: 100 }, // 품명모드: all / allow / block
              { data: 7, width: 200 } // 품명목록 (쉼표 구분, 예: 수출, 내수)
            ]}
            copyPaste={true}
            stretchH="none"
            afterOnCellMouseDown={(e, coords) => {
              if (coords.col === 4 || coords.col === 5) {
                handleBoolCellToggle(coords.row, coords.col);
              }
            }}
            beforeChange={(changes, source) => {
              if (source !== 'edit' || !changes || !Array.isArray(changes) || changes.length === 0) return;
              const [[row, col, oldVal, newVal]] = changes;
              if (col === 6 && newVal !== 'all' && newVal !== 'allow' && newVal !== 'block') {
                (changes as [number, number, unknown, string][])[0][3] = 'all';
              }
            }}
          />
        </div>
      )}
      </>
      )}

      {adminTab === 'manage' && (
        <AdminDepartmentManageTab customerDeptOptions={customerDeptOptions} />
      )}

      {adminTab === 'departments' && (
        <div style={{ flex: '1 1 0', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, color: '#334155' }}>
              거래처
              <select
                value={selectedCustomerId}
                onChange={e => setSelectedCustomerId(e.target.value)}
                disabled={companiesLoading}
                style={{
                  minWidth: 280,
                  padding: '10px 12px',
                  fontSize: 15,
                  borderRadius: 8,
                  border: '1px solid #cbd5e1',
                  background: '#fff',
                }}
              >
                <option value="">{companiesLoading ? '불러오는 중…' : '거래처를 선택하세요'}</option>
                {customerDeptOptions.map(c => (
                  <option key={c.customerId} value={c.customerId}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {selectedCustomerId ? (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: 10,
                  marginBottom: 12,
                  alignItems: 'end',
                }}
              >
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 600, color: '#475569' }}>
                  로그인 이메일 (필수)
                  <input
                    type="email"
                    autoComplete="off"
                    value={newSubEmail}
                    onChange={e => setNewSubEmail(e.target.value)}
                    placeholder="메일형식으로 입력"
                    style={{ padding: '10px 12px', fontSize: 15, borderRadius: 8, border: '1px solid #cbd5e1' }}
                  />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 600, color: '#475569' }}>
                  비밀번호 (6자 이상)
                  <input
                    type="text"
                    autoComplete="off"
                    value={newSubPassword}
                    onChange={e => setNewSubPassword(e.target.value)}
                    placeholder="••••••••"
                    style={{ padding: '10px 12px', fontSize: 15, borderRadius: 8, border: '1px solid #cbd5e1' }}
                  />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 600, color: '#475569' }}>
                  부서 이름 (예: 보세, 수입)
                  <input
                    type="text"
                    value={newSubName}
                    onChange={e => setNewSubName(e.target.value)}
                    placeholder="선택"
                    style={{ padding: '10px 12px', fontSize: 15, borderRadius: 8, border: '1px solid #cbd5e1' }}
                  />
                </label>
                <button
                  type="button"
                  onClick={() => void handleCreateDepartmentAccount()}
                  disabled={deptSaving}
                  style={{
                    padding: '10px 20px',
                    fontSize: 15,
                    fontWeight: 600,
                    border: 'none',
                    borderRadius: 8,
                    cursor: deptSaving ? 'wait' : 'pointer',
                    background: '#1976d2',
                    color: '#fff',
                    opacity: deptSaving ? 0.7 : 1,
                    height: 42,
                    alignSelf: 'end',
                  }}
                >
                  {deptSaving ? '처리 중…' : '부서 계정 추가'}
                </button>
              </div>
              <h3 style={{ fontWeight: 700, fontSize: 18, color: '#1976d2', margin: '0 0 10px 0' }}>부서(서브) 계정 목록</h3>
              <div
                style={{
                  flex: '1 1 0',
                  minHeight: 200,
                  overflow: 'auto',
                  border: '1px solid #e5e7eb',
                  borderRadius: 8,
                  background: '#fafafa',
                }}
              >
                {departmentsLoading ? (
                  <div style={{ padding: 24, color: '#64748b' }}>불러오는 중…</div>
                ) : departmentAccounts.length === 0 ? (
                  <div style={{ padding: 24, color: '#64748b' }}>
                    등록된 부서 전용 계정이 없습니다. 위에서 이메일·비밀번호·부서 이름을 입력해 추가하세요.
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 15 }}>
                    <thead>
                      <tr style={{ background: '#e3f2fd', position: 'sticky', top: 0 }}>
                        <th style={{ textAlign: 'left', padding: '12px 16px', borderBottom: '1px solid #90caf9' }}>이메일</th>
                        <th style={{ textAlign: 'left', padding: '12px 16px', borderBottom: '1px solid #90caf9' }}>비밀번호</th>
                        <th style={{ textAlign: 'left', padding: '12px 16px', borderBottom: '1px solid #90caf9' }}>부서 이름</th>
                        <th style={{ textAlign: 'left', padding: '12px 16px', borderBottom: '1px solid #90caf9', width: 200 }}>등록일</th>
                        <th style={{ textAlign: 'right', padding: '12px 16px', borderBottom: '1px solid #90caf9', width: 100 }}>삭제</th>
                      </tr>
                    </thead>
                    <tbody>
                      {departmentAccounts.map(row => (
                        <tr key={row.email} style={{ background: '#fff', borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '12px 16px', fontWeight: 600, fontSize: 14 }}>{row.email}</td>
                          <td style={{ padding: '12px 16px', fontFamily: 'ui-monospace, monospace', fontSize: 14 }}>
                            {row.passwordPlain != null && row.passwordPlain !== '' ? row.passwordPlain : '—'}
                          </td>
                          <td style={{ padding: '12px 16px', fontWeight: 600, color: '#334155' }}>
                            {row.displayName || '—'}
                          </td>
                          <td style={{ padding: '12px 16px', color: '#64748b', fontSize: 14 }}>
                            {row.createdAt &&
                            typeof row.createdAt === 'object' &&
                            row.createdAt !== null &&
                            'toDate' in row.createdAt &&
                            typeof (row.createdAt as { toDate: () => Date }).toDate === 'function'
                              ? (row.createdAt as { toDate: () => Date }).toDate().toLocaleString('ko-KR')
                              : '—'}
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                            <button
                              type="button"
                              onClick={() => void handleDeleteDepartmentAccount(row)}
                              style={{
                                padding: '6px 12px',
                                fontSize: 14,
                                border: '1px solid #ef9a9a',
                                borderRadius: 6,
                                background: '#fff',
                                color: '#c62828',
                                cursor: 'pointer',
                                fontWeight: 600,
                              }}
                            >
                              삭제
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          ) : (
            <div style={{ padding: 20, color: '#64748b', background: '#f8fafc', borderRadius: 8, border: '1px dashed #cbd5e1' }}>
              위에서 거래처를 선택하면 부서 계정을 추가할 수 있습니다.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminPage; 